import { Link } from "react-router-dom";
import FarmersNav from "@/components/FarmersNav";
import {
  ArrowRight, Sparkle, UsersThree, Basket, Trophy, Handshake, ShoppingBag,
  Storefront, ChartLine, Warning, Bed, ForkKnife, Confetti, Sun,
  GraduationCap, Lightbulb, Newspaper, Bank, IdentificationBadge, Wrench,
  Truck, Warehouse, FileText, Coins, MapTrifold, Notebook, House,
  ChatCircleDots, Camera, Leaf, Package, Bug, Drop, Cow, Plant, MapPin, Receipt,
} from "@phosphor-icons/react";

/**
 * FarmersModules — Farmers Pulse's rich module hub pages.
 *
 * Each named export renders a themed "shelf" page for the corresponding
 * top-level module: hero, 4-6 topic cards, and a call-to-action. Fully
 * navigable placeholders that give users a clear picture of what's coming
 * on each surface while backend endpoints roll in incrementally.
 *
 * Structure mirrors the user-authored IA:
 *  Sell & Earn: Products · Deals · Surplus · Marketplace · Farm Stand · Buyers · Needs
 *  Visit & Experience: Farm Stay · Farm Dining · Experiences · Events
 *  Knowledge: Almanac · Learning · Research · Innovation
 *  Support: Government · Farm Help · Jobs · Services · Cooperatives · Transport · Storage
 *  Tools: Documents · Finance · Journal · Reports
 *  Network / Community: Network · Community · Buyers Directory
 */
function Shell({ testid, eyebrow, Icon, title, subtitle, intro, cards, cta }) {
  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 px-5 md:px-10 bg-[#F5EBDC]" data-testid={testid}>
      <div className="max-w-6xl mx-auto"><FarmersNav /></div>
      <div className="max-w-6xl mx-auto mt-6">
        <header>
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            <Icon size={12} weight="fill" className="inline mr-1" /> {eyebrow}
          </div>
          <h1 className="mt-1 font-serif-display text-4xl md:text-5xl leading-[1.02] tracking-tight">{title}</h1>
          <p className="mt-2 max-w-3xl text-[#3A3830]">{subtitle}</p>
          {intro && <p className="mt-2 max-w-3xl text-[#3A3830]">{intro}</p>}
        </header>

        <section className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {cards.map((c) => {
            const CardIcon = c.Icon || Sparkle;
            const inner = (
              <div className={`rounded-2xl border p-5 h-full ${c.to ? "bg-white border-black/10 hover:border-[color:var(--np-brand,#B85F2A)] hover:shadow-sm transition" : "bg-white/60 border-black/10"}`}>
                <div className="flex items-center gap-2 text-[color:var(--np-brand,#B85F2A)]">
                  <CardIcon size={16} weight="fill" />
                  <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]">
                    {c.eyebrow}
                  </div>
                  {c.badge && !c.to && (
                    <span className="ml-auto rounded-full bg-[color:var(--np-brand,#B85F2A)]/10 text-[color:var(--np-brand,#B85F2A)] px-2 py-0.5 font-mono-tech text-[8px] tracking-[0.24em] uppercase">
                      {c.badge}
                    </span>
                  )}
                </div>
                <div className="mt-2 font-serif-display text-xl">{c.title}</div>
                <p className="mt-1 text-sm text-[#3A3830]">{c.description}</p>
                {c.to && (
                  <div className="mt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                    {c.linkLabel || "Open"} <ArrowRight size={10} weight="bold" />
                  </div>
                )}
              </div>
            );
            return c.to
              ? <Link key={c.title} to={c.to} data-testid={`${testid}-card-${idOf(c.title)}`}>{inner}</Link>
              : <div key={c.title} data-testid={`${testid}-card-${idOf(c.title)}`}>{inner}</div>;
          })}
        </section>

        {cta && (
          <section className="mt-10 rounded-3xl bg-[#1B4332] text-[#F5EBDC] p-6 md:p-8" data-testid={`${testid}-cta`}>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">{cta.eyebrow}</div>
            <h2 className="mt-1 font-serif-display text-2xl md:text-3xl">{cta.title}</h2>
            <p className="mt-2 max-w-2xl text-[#E7DDCA]">{cta.description}</p>
            <Link to={cta.to || "/farmers"}
                  className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase">
              {cta.label} <ArrowRight size={11} weight="bold" />
            </Link>
          </section>
        )}
      </div>
    </main>
  );
}

const idOf = (s) => (s || "").toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/* =====================================================================
 *  Network
 * ===================================================================== */
export function FarmersNetwork() {
  return <Shell
    testid="farmers-network-page" eyebrow="Network" Icon={UsersThree}
    title="Meet the people behind every farm, food business & field."
    subtitle="Discover farmers, buyers, cooperatives, agronomists, veterinarians, extension officers, researchers and local agricultural communities near you."
    intro="Network is where connections happen — from a smallholder in Kerala looking for a wholesale buyer, to an alumnus from an agri college mentoring a student project."
    cards={[
      { eyebrow: "Farmers", title: "Find nearby farmers", Icon: UsersThree, to: "/farmers/geo-tree",
        description: "Drill Country → State → District → City → Village to see every registered farm in your area.", linkLabel: "Open Geo Tree" },
      { eyebrow: "Experts", title: "Agronomists & researchers", Icon: Lightbulb, to: "/experts",
        description: "Ask verified experts, follow their posts, invite them to your farm.", linkLabel: "Open experts" },
      { eyebrow: "Institutions", title: "Schools & colleges", Icon: GraduationCap, to: "/geo",
        description: "Educational institutions running Field Task projects & Garden Manager." },
      { eyebrow: "Cooperatives", title: "Local cooperatives", Icon: Handshake, to: "/farmers/cooperatives",
        description: "Farmer collectives for shared equipment, bulk purchasing, group selling." },
      { eyebrow: "Buyers", title: "Restaurants & wholesalers", Icon: Handshake, to: "/farmers/buyers",
        description: "Chefs, grocers, schools, food services looking for local farm products." },
      { eyebrow: "Community", title: "Groups & discussions", Icon: ChatCircleDots, to: "/farmers/community",
        description: "Crop-based, regional and topic-based farmer groups." },
    ]}
  />;
}

/* =====================================================================
 *  Sell & Earn — Products / Deals / Surplus / Marketplace / Farm Stand / Buyers / Needs
 * ===================================================================== */
export function FarmersProducts() {
  return <Shell
    testid="farmers-products-page" eyebrow="Products" Icon={Basket}
    title="Everything your farm sells."
    subtitle="Fresh produce, farm boxes, seedlings, processed foods, dairy, honey, eggs, plants — one shelf that mirrors your Inventory."
    cards={[
      { eyebrow: "My shop", title: "Manage products", Icon: Storefront, to: "/farmers/websites",
        description: "Add, edit, publish and photograph every product your farm sells.", linkLabel: "Open shop editor" },
      { eyebrow: "Orders", title: "Order inbox", Icon: Receipt, to: "/farmers/orders",
        description: "Every paid checkout from your farm site — pack it, hand it over, mark it fulfilled.", linkLabel: "Open orders" },
      { eyebrow: "Inventory-linked", title: "Sell from inventory", Icon: Package, to: "/farmers/inventory",
        description: "Turn any inventory item into a product with one tap.", linkLabel: "Open inventory" },
      { eyebrow: "Fresh today", title: "Today's freshness", Icon: Sun, to: "/farmers/today",
        description: "Flag what came out of the field today — buyers love the freshness signal." },
      { eyebrow: "Farm boxes", title: "CSA boxes", Icon: Basket, badge: "Coming next",
        description: "Weekly / monthly subscription boxes with rotating produce." },
      { eyebrow: "Seedlings", title: "Seedlings & plants", Icon: Plant, badge: "Coming next",
        description: "Nursery stock, transplants, ornamentals, herbs." },
      { eyebrow: "Processed", title: "Preserves & processed", Icon: ForkKnife, badge: "Coming next",
        description: "Jams, honey, pickles, cheese, ghee — the value-add shelf." },
    ]}
    cta={{ eyebrow: "Sell more", title: "Publish a product in 60 seconds", description: "Add a photo, a price, a unit — done.", label: "Add a product", to: "/farmers/websites/new" }}
  />;
}

export function FarmersDeals() {
  return <Shell
    testid="farmers-deals-page" eyebrow="Deals" Icon={Trophy}
    title="Discounts that move product before it moves on you."
    subtitle="End-of-day specials, bulk offers, seasonal promotions, community coupons — every deal is time-bound and location-aware."
    cards={[
      { eyebrow: "End-of-day", title: "Sunset specials", Icon: Sun, badge: "Coming next",
        description: "Last-call discounts on today's produce that won't survive another day." },
      { eyebrow: "Bulk", title: "Bulk pricing", Icon: Basket, badge: "Coming next",
        description: "Wholesale pricing tiers for buyers ordering full crates or above." },
      { eyebrow: "Seasonal", title: "Seasonal promos", Icon: Confetti, badge: "Coming next",
        description: "Mango week, harvest festival, first-frost fire-sale." },
      { eyebrow: "Coupons", title: "Community coupons", Icon: Trophy, badge: "Coming next",
        description: "Digital coupons shared to farmer WhatsApp / co-op groups." },
    ]}
  />;
}

export function FarmersSurplus() {
  return <Shell
    testid="farmers-surplus-page" eyebrow="Surplus" Icon={Handshake}
    title="Never waste a harvest again."
    subtitle="Excess produce, discounted goods, food-bank donations, animal-feed conversion, processing partnerships — surplus gets rerouted, not composted."
    cards={[
      { eyebrow: "Food banks", title: "Donate to food banks", Icon: Handshake, badge: "Coming next",
        description: "Local food banks and community fridges near you." },
      { eyebrow: "Discount", title: "Blemished & seconds", Icon: Basket, badge: "Coming next",
        description: "Perfectly fine produce with cosmetic imperfections — deeply discounted." },
      { eyebrow: "Animal feed", title: "Livestock feed exchange", Icon: Cow, badge: "Coming next",
        description: "Divert leaf trim, vegetable seconds and grain to local livestock farms." },
      { eyebrow: "Processing", title: "Processing partners", Icon: ForkKnife, badge: "Coming next",
        description: "Local jam-makers, saucers, dryers, freezers — turn surplus into shelf-life." },
    ]}
  />;
}

export function FarmersMarketplaceHub() {
  return <Shell
    testid="farmers-marketplace-page" eyebrow="Marketplace" Icon={ShoppingBag}
    title="Buy & sell — beyond just produce."
    subtitle="Equipment, tractors, vehicles, tools, livestock, seeds, plants, farm supplies — the wider farm marketplace, moderated and local."
    cards={[
      { eyebrow: "Equipment", title: "Tractors & tools", Icon: Wrench, badge: "Coming next",
        description: "New, used, rental — from tillers to combine harvesters." },
      { eyebrow: "Livestock", title: "Livestock market", Icon: Cow, badge: "Coming next",
        description: "Cattle, poultry, goats, sheep, pigs, aquaculture." },
      { eyebrow: "Seeds", title: "Seeds & saplings", Icon: Plant, badge: "Coming next",
        description: "Certified, heirloom, saved-seed exchanges." },
      { eyebrow: "Supplies", title: "Inputs & supplies", Icon: Package, badge: "Coming next",
        description: "Fertiliser, compost, mulch, packaging, containers." },
    ]}
  />;
}

export function FarmersFarmStand() {
  return <Shell
    testid="farmers-farm-stand-page" eyebrow="Farm Stand" Icon={Storefront}
    title="Farm-gate sales, done right."
    subtitle="Location, hours, Fresh Today board, pickup instructions, payments — everything a customer needs to walk up to your farm."
    cards={[
      { eyebrow: "Hours", title: "Opening hours", Icon: Sun, badge: "Coming next",
        description: "Weekly schedule with holiday overrides." },
      { eyebrow: "Location", title: "Directions & map", Icon: MapPin, badge: "Coming next",
        description: "Pin, address, driving directions, parking notes." },
      { eyebrow: "Fresh Today", title: "Today's board", Icon: Basket, to: "/farmers/today",
        description: "Live 'what's in today' list synced to inventory." },
      { eyebrow: "Payments", title: "Payments accepted", Icon: Coins, badge: "Coming next",
        description: "Cash, UPI, Venmo, cards — set expectations." },
    ]}
  />;
}

export function FarmersBuyers() {
  return <Shell
    testid="farmers-buyers-page" eyebrow="Buyers" Icon={Handshake}
    title="Chefs, grocers, schools & food services buying local."
    subtitle="A curated directory of buyers actively sourcing from local farms — restaurants, food halls, grocers, cooperatives, hospitals, universities and institutional food services."
    cards={[
      { eyebrow: "Restaurants", title: "Restaurants & chefs", Icon: ForkKnife, badge: "Coming next",
        description: "Farm-to-table chefs sourcing produce, dairy, meat, eggs." },
      { eyebrow: "Wholesalers", title: "Wholesale grocers", Icon: ShoppingBag, badge: "Coming next",
        description: "Neighborhood grocers, co-ops, natural food markets." },
      { eyebrow: "Institutions", title: "Schools & hospitals", Icon: GraduationCap, badge: "Coming next",
        description: "Farm-to-school and farm-to-hospital programs." },
      { eyebrow: "Post RFQ", title: "Post a buying request", Icon: Sparkle, badge: "Coming next",
        description: "Buyers can post 'I need 200kg tomatoes weekly' and get farm quotes." },
    ]}
  />;
}

export function FarmersNeeds() {
  return <Shell
    testid="farmers-needs-page" eyebrow="Needs" Icon={Warning}
    title="Post what you need — the community answers."
    subtitle="Workers for harvest weekend, a tractor for two days, storage for a bumper crop, a buyer for excess mangoes — farmers post needs, the network responds."
    cards={[
      { eyebrow: "Labor", title: "Workers & helpers", Icon: UsersThree, badge: "Coming next",
        description: "Seasonal harvesters, planters, packers, drivers." },
      { eyebrow: "Equipment", title: "Rent equipment", Icon: Wrench, badge: "Coming next",
        description: "Short-term rentals of tractors, sprayers, mowers." },
      { eyebrow: "Storage", title: "Emergency storage", Icon: Warehouse, badge: "Coming next",
        description: "Cold rooms, silos, warehouses with spare capacity." },
      { eyebrow: "Buyers", title: "Bulk buyer needed", Icon: Handshake, badge: "Coming next",
        description: "Broadcast a surplus to nearby buyers with one tap." },
      { eyebrow: "Seeds", title: "Seed exchange", Icon: Plant, badge: "Coming next",
        description: "Ask for a seed variety you can't find locally." },
      { eyebrow: "Transport", title: "Transport request", Icon: Truck, badge: "Coming next",
        description: "Trucks going your way — share the ride, split the cost." },
    ]}
  />;
}

/* =====================================================================
 *  Visit & Experience
 * ===================================================================== */
export function FarmersFarmStay() {
  return <Shell
    testid="farmers-farm-stay-page" eyebrow="Farm Stay" Icon={Bed}
    title="Rooms, cabins, campsites & RV parking on working farms."
    subtitle="Agritourism — turn a spare room, barn loft, or field into a revenue stream. Reservations, calendars, house rules, payments."
    cards={[
      { eyebrow: "Rooms", title: "Rooms & cabins", Icon: Bed, badge: "Coming next",
        description: "Simple rooms, farmhouse suites, glamping cabins." },
      { eyebrow: "Camping", title: "Camping & tents", Icon: Confetti, badge: "Coming next",
        description: "Tent sites in orchards, meadows, forest edges." },
      { eyebrow: "RV", title: "RV parking", Icon: Truck, badge: "Coming next",
        description: "Hookups, dump stations, quiet parking on the farm." },
      { eyebrow: "Calendar", title: "Availability calendar", Icon: Sun, badge: "Coming next",
        description: "Multi-night reservations with harvest-season blackouts." },
    ]}
  />;
}

export function FarmersFarmDining() {
  return <Shell
    testid="farmers-farm-dining-page" eyebrow="Farm Dining" Icon={ForkKnife}
    title="Farm-to-table meals, dinners & tastings."
    subtitle="Sunday brunches, harvest dinners, cheese tastings, olive-oil pressings, wine pairings — hosted right on the farm."
    cards={[
      { eyebrow: "Meals", title: "Weekly farm meals", Icon: ForkKnife, badge: "Coming next",
        description: "Fixed-menu meals with ingredients grown steps away." },
      { eyebrow: "Events", title: "Harvest dinners", Icon: Confetti, badge: "Coming next",
        description: "Long-table dinners in the field, in season." },
      { eyebrow: "Tastings", title: "Tastings & pressings", Icon: Basket, badge: "Coming next",
        description: "Wine, cheese, olive oil, honey — direct from producer." },
      { eyebrow: "Reservations", title: "Reservations & waitlists", Icon: Sun, badge: "Coming next",
        description: "Bookings tied to your farm calendar." },
    ]}
  />;
}

export function FarmersExperiences() {
  return <Shell
    testid="farmers-experiences-page" eyebrow="Experiences" Icon={Confetti}
    title="Pick-your-own, tours, workshops, school visits."
    subtitle="Give visitors a story to take home — one they can taste, touch and photograph."
    cards={[
      { eyebrow: "U-Pick", title: "Pick-your-own", Icon: Basket, badge: "Coming next",
        description: "Strawberry, blueberry, apple, sunflower fields open to the public." },
      { eyebrow: "Tours", title: "Farm tours", Icon: MapTrifold, badge: "Coming next",
        description: "Guided walks — dairy, orchard, permaculture, native flora." },
      { eyebrow: "Workshops", title: "Hands-on workshops", Icon: GraduationCap, badge: "Coming next",
        description: "Cheese-making, soap-making, herb-drying, pruning." },
      { eyebrow: "Schools", title: "School field trips", Icon: GraduationCap, to: "/geo",
        description: "Structured visits for schools — links into the NaturePulse School Hub.",
        linkLabel: "Open school hub" },
      { eyebrow: "Photography", title: "Photographer days", Icon: Camera, badge: "Coming next",
        description: "Golden-hour photo access — weddings, portraits, editorial." },
    ]}
  />;
}

export function FarmersEvents() {
  return <Shell
    testid="farmers-events-page" eyebrow="Events" Icon={Confetti}
    title="Fairs, farm shows, workshops, field days."
    subtitle="Local events on and off the farm — training days, agricultural fairs, farm shows, conferences, field days."
    cards={[
      { eyebrow: "Fairs", title: "Agricultural fairs", Icon: Confetti, badge: "Coming next",
        description: "Regional agri fairs, livestock shows, seed exchanges." },
      { eyebrow: "Conferences", title: "Conferences", Icon: Lightbulb, badge: "Coming next",
        description: "Sustainability, regenerative, precision-ag conferences." },
      { eyebrow: "Field days", title: "On-farm field days", Icon: Plant, badge: "Coming next",
        description: "Extension programs, cover-crop demos, variety trials." },
      { eyebrow: "Post an event", title: "List your event", Icon: Sparkle, badge: "Coming next",
        description: "Any farmer or organisation can post events, moderated regionally." },
    ]}
  />;
}

/* =====================================================================
 *  Knowledge — Almanac + Learning + Innovation
 * ===================================================================== */
export function FarmersAlmanac() {
  return <Shell
    testid="farmers-almanac-page" eyebrow="Almanac" Icon={Sun}
    title="Seasonal farming intelligence, sun & moon rhythms, local wisdom."
    subtitle="Sunrise / sunset, moon phases, historical conditions, planting traditions, long-range seasonal outlooks — pooled across farmers, generations and continents."
    cards={[
      { eyebrow: "This year", title: "The Living Almanac", Icon: Sun, to: "/farmers/almanac-full",
        description: "The full editorial article on farming rhythms.",
        linkLabel: "Read the article" },
      { eyebrow: "Moon", title: "Moon phases & tides", Icon: Sun, badge: "Coming next",
        description: "Plant by the moon, harvest by the tide." },
      { eyebrow: "Historical", title: "Historical conditions", Icon: Notebook, badge: "Coming next",
        description: "This week last year — rainfall, temperature, first frost, first bloom." },
      { eyebrow: "Traditions", title: "Regional traditions", Icon: Notebook, badge: "Coming next",
        description: "Ancestral planting wisdom, festivals, first-harvest observances." },
      { eyebrow: "Newsroom", title: "Farmers' Almanac news", Icon: Newspaper, to: "/farmers/news",
        description: "Fresh from farmersalmanac.com and other seasonal publishers.",
        linkLabel: "Open feed" },
    ]}
  />;
}

export function FarmersLearning() {
  return <Shell
    testid="farmers-learning-page" eyebrow="Learning" Icon={GraduationCap}
    title="Guides, videos, courses & how-to for every farm."
    subtitle="Crop lessons, soil science, irrigation, composting, farm business, marketing, animal care — bite-sized and searchable."
    cards={[
      { eyebrow: "Crops", title: "Crop guides", Icon: Plant, to: "/farmers/crops",
        description: "Detailed pages for 24+ crops with growing zones and calendars.",
        linkLabel: "Open crop database" },
      { eyebrow: "Soil", title: "Soil & compost", Icon: Leaf, to: "/farmers/soil",
        description: "pH, nutrients, structure, cover crops, composting." },
      { eyebrow: "Irrigation", title: "Water & irrigation", Icon: Drop, to: "/farmers/water",
        description: "Drip, sprinkler, flood, ET-based scheduling." },
      { eyebrow: "Business", title: "Farm business", Icon: Coins, badge: "Coming next",
        description: "Marketing, pricing, insurance, taxes, succession." },
      { eyebrow: "Video", title: "Video library", Icon: Sparkle, badge: "Coming next",
        description: "Short field-tested how-to videos, in multiple languages." },
    ]}
  />;
}

export function FarmersInnovation() {
  return <Shell
    testid="farmers-innovation-page" eyebrow="Innovation" Icon={Sparkle}
    title="AI, drones, sensors & regenerative techniques."
    subtitle="Precision agriculture, hydroponics, aquaponics, robotics, cover cropping, agroforestry, IoT — the tools reshaping how we farm."
    cards={[
      { eyebrow: "AI & data", title: "AI on the farm", Icon: Sparkle, to: "/farmers/ask",
        description: "Ask Farmers AI — trained on agri knowledge + your local context.",
        linkLabel: "Open Ask AI" },
      { eyebrow: "Drones", title: "Drones & sensors", Icon: Sparkle, badge: "Coming next",
        description: "Multispectral drones, soil moisture sensors, weather stations." },
      { eyebrow: "Hydroponics", title: "Hydro & aquaponics", Icon: Drop, badge: "Coming next",
        description: "Vertical farming, deep water culture, aquaponic loops." },
      { eyebrow: "Robotics", title: "Robotics & automation", Icon: Wrench, badge: "Coming next",
        description: "Weeding, harvesting, sorting robots for smallholders." },
      { eyebrow: "Regen", title: "Regenerative practices", Icon: Leaf, badge: "Coming next",
        description: "Cover crops, no-till, holistic grazing, agroforestry, biochar." },
    ]}
  />;
}

/* =====================================================================
 *  Support — Farm Help / Jobs / Services / Cooperatives / Transport / Storage
 * ===================================================================== */
export function FarmersFarmHelp() {
  return <Shell
    testid="farmers-farm-help-page" eyebrow="Farm Help" Icon={UsersThree}
    title="Workers, volunteers, apprentices & contractors."
    subtitle="Seasonal labour for harvest, apprentices seeking hands-on experience, WWOOF-style volunteers, part-time helpers and specialised contractors."
    cards={[
      { eyebrow: "Seasonal", title: "Seasonal workers", Icon: UsersThree, badge: "Coming next",
        description: "Harvest crews, planting crews, pack-house workers." },
      { eyebrow: "Volunteers", title: "Volunteers & WWOOF", Icon: Confetti, badge: "Coming next",
        description: "Room & board in exchange for farm work." },
      { eyebrow: "Apprentices", title: "Apprentices", Icon: GraduationCap, badge: "Coming next",
        description: "Structured 1-2 season apprenticeships." },
      { eyebrow: "Contractors", title: "Contractors", Icon: Wrench, badge: "Coming next",
        description: "Custom-hire operators, spraying, custom baling, fencing." },
    ]}
  />;
}

export function FarmersJobs() {
  return <Shell
    testid="farmers-jobs-page" eyebrow="Jobs" Icon={IdentificationBadge}
    title="Farm employment & agricultural careers."
    subtitle="Permanent, seasonal, internships, apprenticeships — from the smallest farm to the largest agri-tech firm."
    cards={[
      { eyebrow: "Farms", title: "Farm jobs", Icon: Cow, badge: "Coming next",
        description: "Field, dairy, orchard, greenhouse, pack-house openings." },
      { eyebrow: "Agri-tech", title: "Agri-tech careers", Icon: Sparkle, badge: "Coming next",
        description: "Agronomy, IoT, drones, robotics, food-tech startups." },
      { eyebrow: "Extension", title: "Extension & research", Icon: Lightbulb, badge: "Coming next",
        description: "Field officers, university positions, NGO roles." },
      { eyebrow: "Internships", title: "Internships", Icon: GraduationCap, badge: "Coming next",
        description: "Summer, year-round and school-linked farm internships." },
    ]}
  />;
}

export function FarmersServices() {
  return <Shell
    testid="farmers-services-page" eyebrow="Services" Icon={Wrench}
    title="Machinery rental, soil testing, veterinary, transport."
    subtitle="A directory of ag services — soil labs, veterinarians, irrigation contractors, drone operators, mechanics, packing services and more."
    cards={[
      { eyebrow: "Machinery", title: "Machinery rental", Icon: Wrench, badge: "Coming next",
        description: "Tractors, combines, sprayers, mowers, tillers." },
      { eyebrow: "Testing", title: "Soil & water testing", Icon: Drop, to: "/farmers/soil",
        description: "Local labs plus AI PDF extract when you have a report." },
      { eyebrow: "Vets", title: "Veterinary services", Icon: Cow, badge: "Coming next",
        description: "Livestock, poultry, aquaculture veterinarians." },
      { eyebrow: "Drone", title: "Drone services", Icon: Sparkle, badge: "Coming next",
        description: "Aerial imaging, spraying, seeding, mapping." },
      { eyebrow: "Repair", title: "Repairs & fabrication", Icon: Wrench, badge: "Coming next",
        description: "Mobile mechanics, welders, sharpeners." },
    ]}
  />;
}

export function FarmersCooperatives() {
  return <Shell
    testid="farmers-cooperatives-page" eyebrow="Cooperatives" Icon={Handshake}
    title="Farmer collectives, shared equipment, group selling."
    subtitle="Cooperatives multiply what a single farmer can do — bulk-buy inputs, share tractors, negotiate collectively with buyers, pool storage."
    cards={[
      { eyebrow: "Local", title: "Cooperatives near you", Icon: Handshake, to: "/farmers/geo-tree",
        description: "Drill into your district on the Geographic Tree.",
        linkLabel: "Open Geo Tree" },
      { eyebrow: "Shared", title: "Shared equipment", Icon: Wrench, badge: "Coming next",
        description: "Coops with tractors, threshers, cold rooms available to members." },
      { eyebrow: "Bulk", title: "Bulk purchasing", Icon: Package, badge: "Coming next",
        description: "Group orders for seeds, fertiliser, packaging." },
      { eyebrow: "Sell", title: "Group selling", Icon: Basket, badge: "Coming next",
        description: "Negotiate collective contracts with wholesalers." },
    ]}
  />;
}

export function FarmersTransport() {
  return <Shell
    testid="farmers-transport-page" eyebrow="Transport & Logistics" Icon={Truck}
    title="Pickup, delivery, cold-chain, shared transport."
    subtitle="Get product to market — routes, cold storage, refrigerated trucks, shared runs with nearby farms."
    cards={[
      { eyebrow: "Trucks", title: "Truck & pickup runs", Icon: Truck, badge: "Coming next",
        description: "Regular runs to nearby markets — book seats for your crates." },
      { eyebrow: "Cold-chain", title: "Refrigerated transport", Icon: Drop, badge: "Coming next",
        description: "Refrigerated trucks for dairy, meat, fresh produce." },
      { eyebrow: "Shared", title: "Shared runs", Icon: UsersThree, badge: "Coming next",
        description: "Pool transport with neighbours — cheaper per kg." },
      { eyebrow: "Post", title: "Request transport", Icon: Sparkle, badge: "Coming next",
        description: "Post a load & date, receive quotes." },
    ]}
  />;
}

export function FarmersStorage() {
  return <Shell
    testid="farmers-storage-page" eyebrow="Storage" Icon={Warehouse}
    title="Cold rooms, warehouses, silos, shared capacity."
    subtitle="Every farm has occasional storage crunches — this shelf makes cold rooms, warehouses and silos discoverable."
    cards={[
      { eyebrow: "Cold", title: "Cold storage", Icon: Drop, badge: "Coming next",
        description: "Local cold rooms with day/week/month pricing." },
      { eyebrow: "Dry", title: "Warehouses & barns", Icon: Warehouse, badge: "Coming next",
        description: "Dry storage for grain, packaged goods, equipment." },
      { eyebrow: "Silos", title: "Grain silos", Icon: Warehouse, badge: "Coming next",
        description: "Silo capacity — full or partial — near you." },
      { eyebrow: "Request", title: "Request storage", Icon: Warning, badge: "Coming next",
        description: "Post an emergency storage need to nearby farms." },
    ]}
  />;
}

/* =====================================================================
 *  Tools — Documents / Finance / Journal / Reports
 * ===================================================================== */
export function FarmersDocuments() {
  return <Shell
    testid="farmers-documents-page" eyebrow="Documents" Icon={FileText}
    title="Certifications, licenses, insurance, manuals — one filing cabinet."
    subtitle="Upload once, find forever — organic certificates, insurance policies, equipment manuals, soil reports, invoices, warranties, inspection records."
    cards={[
      { eyebrow: "Certifications", title: "Organic & GAP", Icon: FileText, badge: "Coming next",
        description: "Track certification cycles, expiry dates, audit notes." },
      { eyebrow: "Insurance", title: "Farm insurance", Icon: FileText, badge: "Coming next",
        description: "Crop insurance, equipment, liability, disaster policies." },
      { eyebrow: "Manuals", title: "Equipment manuals", Icon: Wrench, badge: "Coming next",
        description: "PDF manuals filed by asset for one-tap retrieval." },
      { eyebrow: "Invoices", title: "Invoices & receipts", Icon: Coins, badge: "Coming next",
        description: "Auto-scanned invoices linked to expenses." },
      { eyebrow: "Reports", title: "Soil & water reports", Icon: Drop, to: "/farmers/soil",
        description: "AI PDF-extract already available for soil lab reports." },
    ]}
  />;
}

export function FarmersFinance() {
  return <Shell
    testid="farmers-finance-page" eyebrow="Farm Finance" Icon={Coins}
    title="Simple books for the farm."
    subtitle="Expenses, income, cost-per-crop, market income, inventory value, profitability summaries — no spreadsheets required."
    cards={[
      { eyebrow: "Expenses", title: "Expense tracker", Icon: Coins, to: "/farmers/mine?tab=expenses",
        description: "24-category expense ledger with per-crop attribution." },
      { eyebrow: "Harvests", title: "Harvest revenue", Icon: Basket, to: "/farmers/mine?tab=harvests",
        description: "Log yields + sale prices to compute revenue per crop." },
      { eyebrow: "Inventory", title: "Inventory value", Icon: Package, to: "/farmers/inventory",
        description: "Snapshot of what's on-hand and what it's worth." },
      { eyebrow: "P&L", title: "Cost & profit summary", Icon: ChartLine, badge: "Coming next",
        description: "Monthly / seasonal P&L with drill-down by crop and market." },
      { eyebrow: "Export", title: "Tax & PDF export", Icon: FileText, badge: "Coming next",
        description: "Year-end statements you can hand to your accountant." },
    ]}
  />;
}

export function FarmersJournal() {
  return <Shell
    testid="farmers-journal-page" eyebrow="Farm Journal" Icon={Notebook}
    title="Daily observations, photos, field notes."
    subtitle="Every farm has its own story — the day the first bee arrived, the day the mango bloomed, the day the well ran dry. The Farm Journal captures all of it."
    cards={[
      { eyebrow: "Diary", title: "Farm diary", Icon: Notebook, to: "/farmers/farms",
        description: "Open your farm → Diary tab for chronological entries.",
        linkLabel: "Open farm registry" },
      { eyebrow: "Observations", title: "My observations", Icon: Sparkle, to: "/farmers/mine?tab=observations",
        description: "Everything you've reported — mapped and tagged." },
      { eyebrow: "Photos", title: "Photo timeline", Icon: Camera, badge: "Coming next",
        description: "Chronological photo wall per crop, field or animal." },
      { eyebrow: "Seasonal", title: "Season summaries", Icon: Sun, badge: "Coming next",
        description: "Auto-generated season write-ups from your diary + harvests." },
    ]}
  />;
}

export function FarmersReports() {
  return <Shell
    testid="farmers-reports-page" eyebrow="Reports" Icon={FileText}
    title="PDF & CSV reports for every corner of the farm."
    subtitle="Inventory, crops, harvests, sales, waste, donations, equipment, markets, rainfall, expenses — export what you need, when you need it."
    cards={[
      { eyebrow: "Inventory", title: "Inventory CSV", Icon: Package, to: "/farmers/inventory",
        description: "The universal inventory can already export CSV.",
        linkLabel: "Open inventory" },
      { eyebrow: "Harvest", title: "Harvest yield report", Icon: Basket, to: "/farmers/mine?tab=harvests",
        description: "Totals per crop, per unit, per year." },
      { eyebrow: "Expense", title: "Expense summary", Icon: Coins, to: "/farmers/mine?tab=expenses",
        description: "By category, by crop, by month, by currency." },
      { eyebrow: "Rainfall", title: "Rainfall history", Icon: Drop, to: "/farmers/water",
        description: "Auto-generated from Open-Meteo archive + your logs." },
      { eyebrow: "Annual", title: "Annual farm report", Icon: FileText, badge: "Coming next",
        description: "End-of-year PDF pulling everything together." },
    ]}
  />;
}
